import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Shell, { StatCard, Section } from "@/pages/Shell";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AuthImg from "@/components/constructor/AuthImg";
import UploadsManager from "@/components/constructor/UploadsManager";
import { EstadoDeObraPanel } from "@/components/construction/ConstructionProgressPublisher";
import { villaProgress } from "@/lib/constructionProgress";
import {
  House, Calendar, FileText, Coin, Receipt, Image as ImageIcon,
  ArrowLeft, User, CheckCircle, Clock, Buildings, TrendUp, Plus, X,
} from "@phosphor-icons/react";

// Legacy phase → readable label. Mirrors the small set we surface on PagosTab;
// duplicated locally to keep this PR self-contained (future cleanup PR may
// consolidate into a shared lib).
const LEGACY_PHASE_LABEL = {
  reservation: "Reserva",
  foundation: "Cimentación",
  structure: "Estructura",
  handover: "Entrega",
};

// Visible name of a payment row. Prefers `title`, falls back to a translated
// legacy phase, finally to a generic "Pago" label. Never renders the raw
// "other" sentinel.
function paymentLabel(p) {
  const title = (p?.title || "").trim();
  if (title) return title;
  const phase = (p?.phase || "").trim();
  if (!phase || phase === "other") return "Pago";
  if (LEGACY_PHASE_LABEL[phase]) return LEGACY_PHASE_LABEL[phase];
  return phase;
}

const TABS_ES = [
  { key: "overview", label: "Resumen", icon: House },
  { key: "progress", label: "Progreso obra", icon: Buildings },
  { key: "documents", label: "Documentos", icon: FileText },
  { key: "payments", label: "Pagos", icon: Coin },
  { key: "invoices", label: "Facturas obra", icon: Receipt },
  { key: "gallery", label: "Galería", icon: ImageIcon },
];
const TABS_EN = [
  { key: "overview", label: "Overview", icon: House },
  { key: "progress", label: "Construction", icon: Buildings },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "payments", label: "Payments", icon: Coin },
  { key: "invoices", label: "Work invoices", icon: Receipt },
  { key: "gallery", label: "Gallery", icon: ImageIcon },
];

const I18N = {
  es: {
    loading: "Cargando…",
    back: "Volver",
    backDash: "Volver al dashboard",
    investorLbl: "Inversor",
    price: "Precio",
    statusLbl: { sold: "Vendida", reserved: "Reservada", available: "Disponible" },
    kpis: "Indicadores",
    kpiProgress: "Avance obra",
    pendingUpdate: "Pendiente de actualización",
    milestonePhase: { preparacion: "Terreno", cimentacion: "Cimentación", estructura: "Estructura", cubierta: "Cubierta", acabados: "Acabados", entrega: "Entrega" },
    kpiPaid: "Pagado",
    kpiPending: "Pendiente",
    kpiDocs: "Documentos",
    paymentsCount: (n) => `${n} pagos`,
    pendingCount: (n) => `${n} por cobrar`,
    updatesCount: (n) => `${n} updates`,
    timelineTitle: "Timeline de esta villa",
    timelineSub: "Evolución real del proyecto.",
    progressEventTitle: (phase) => `Avance obra · ${phase}`,
    paymentEventTitle: (phase) => `Pago recibido · ${phase}`,
    noEvents: "Aún no hay eventos registrados.",
    progressTitle: "Progreso",
    progressDone: "Sin updates aún.",
    progressListTitle: "Avances registrados",
    pctCompleted: "% completado",
    uploadedBy: (name) => `Subido por ${name}`,
    categories: { contracts: "Contratos", legal: "Legal", licenses: "Licencias", construction: "Obra", financials: "Financials", faq: "FAQ", other: "Otros" },
    dataRoomTitle: "Data room de la villa",
    dataRoomSub: "Documentos organizados por categoría.",
    noDocs: "Sin documentos aún.",
    version: "v",
    openDoc: "Abrir documento",
    whoSaw: "Quién lo ha visto (solo admin)",
    viewsLine: (total, users) => `${total} vistas totales · ${users} usuarios únicos`,
    uploadedOn: (date) => `Subido ${date}`,
    by: "por",
    docVersion: (v) => `Versión ${v}`,
    paymentsTitle: "Pagos",
    paymentsEmpty: "Sin pagos registrados.",
    paymentsSchedule: "Calendario de pagos",
    phase: "Fase",
    dueDate: "Vencimiento",
    amount: "Importe",
    status: "Estado",
    payStatus: { paid: "Pagado", overdue: "Vencido", pending: "Pendiente" },
    invoicesTitle: "Facturas de obra asociadas",
    invoicesEmpty: "Sin facturas de obra aún.",
    invoiceDate: "Fecha",
    invoiceProvider: "Proveedor",
    invoiceConcept: "Concepto",
    galleryTitle: "Galería",
    galleryEmpty: "Aún no hay fotos de la obra.",
    galleryCount: (n) => `Galería · ${n} fotos`,
    dateLocale: "es-ES",
  },
  en: {
    loading: "Loading…",
    back: "Back",
    backDash: "Back to dashboard",
    investorLbl: "Investor",
    price: "Price",
    statusLbl: { sold: "Sold", reserved: "Reserved", available: "Available" },
    kpis: "Indicators",
    kpiProgress: "Construction progress",
    pendingUpdate: "Pending update",
    milestonePhase: { preparacion: "Land preparation", cimentacion: "Foundation", estructura: "Structure", cubierta: "Roof", acabados: "Finishes", entrega: "Handover" },
    kpiPaid: "Paid",
    kpiPending: "Pending",
    kpiDocs: "Documents",
    paymentsCount: (n) => `${n} payment(s)`,
    pendingCount: (n) => `${n} to collect`,
    updatesCount: (n) => `${n} update(s)`,
    timelineTitle: "Villa timeline",
    timelineSub: "Real project evolution.",
    progressEventTitle: (phase) => `Construction update · ${phase}`,
    paymentEventTitle: (phase) => `Payment received · ${phase}`,
    noEvents: "No events logged yet.",
    progressTitle: "Progress",
    progressDone: "No updates yet.",
    progressListTitle: "Construction updates",
    pctCompleted: "% complete",
    uploadedBy: (name) => `Uploaded by ${name}`,
    categories: { contracts: "Contracts", legal: "Legal", licenses: "Licenses", construction: "Construction", financials: "Financials", faq: "FAQ", other: "Other" },
    dataRoomTitle: "Villa data room",
    dataRoomSub: "Documents organised by category.",
    noDocs: "No documents yet.",
    version: "v",
    openDoc: "Open document",
    whoSaw: "Who has viewed it (admin only)",
    viewsLine: (total, users) => `${total} total views · ${users} unique users`,
    uploadedOn: (date) => `Uploaded ${date}`,
    by: "by",
    docVersion: (v) => `Version ${v}`,
    paymentsTitle: "Payments",
    paymentsEmpty: "No payments logged.",
    paymentsSchedule: "Payment schedule",
    phase: "Phase",
    dueDate: "Due date",
    amount: "Amount",
    status: "Status",
    payStatus: { paid: "Paid", overdue: "Overdue", pending: "Pending" },
    invoicesTitle: "Associated work invoices",
    invoicesEmpty: "No work invoices yet.",
    invoiceDate: "Date",
    invoiceProvider: "Provider",
    invoiceConcept: "Concept",
    galleryTitle: "Gallery",
    galleryEmpty: "No photos yet.",
    galleryCount: (n) => `Gallery · ${n} photos`,
    dateLocale: "en-US",
  },
};

const VALID_TABS = new Set(TABS_ES.map((t) => t.key));

export default function VillaDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  // Deep link: /villa/:id?tab=progress (Action Center "Publicar avance")
  // opens the requested tab directly; unknown values fall back to Resumen.
  const [tab, setTab] = useState(() => {
    const requested = searchParams.get("tab");
    return requested && VALID_TABS.has(requested) ? requested : "overview";
  });
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get(`/villas/${id}`);
      setData(data);
    } catch (e) { setErr(formatError(e)); }
  };

  const lang = user?.role === "constructor" ? "en" : "es";
  const t = I18N[lang];

  const accent = user?.role === "admin" ? "#1C1C1E"
    : user?.role === "investor" ? "#2F3E35"
    : user?.role === "constructor" ? "#9C5236" : "#1C1917";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/villas/${id}`);
        setData(data);
      } catch (e) { setErr(formatError(e)); }
    })();
  }, [id]);

  if (err) return (
    <Shell lang={lang}>
      <div className="dv-card text-center py-10">
        <div className="text-red-700 mb-4">{err}</div>
        <button onClick={() => navigate("/")} className="dv-btn dv-btn-primary" data-testid="villa-forbidden-back">
          <ArrowLeft size={16} /> {t.backDash}
        </button>
      </div>
    </Shell>
  );
  if (!data) return <Shell lang={lang}><div className="text-stone-400">{t.loading}</div></Shell>;

  const { villa, owner, summary, progress, payments, documents, invoices } = data;
  // The villa's OWN progress (EstadoDeObraPanel), never the per-photo
  // `latest_progress_pct`: no registered figure = pending, not 0%.
  const ownProgress = villaProgress(villa);
  const TABS = lang === "en" ? TABS_EN : TABS_ES;
  // Hide tabs that don't apply to investors
  const filteredTabs = TABS.filter(tb => user?.role !== "investor" || tb.key !== "invoices");

  return (
    <Shell tabs={filteredTabs} activeTab={tab} onTabChange={setTab} lang={lang}>
      {/* Back + Hero */}
      <button onClick={() => navigate(-1)} data-testid="villa-back-btn"
        className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-900 mb-5">
        <ArrowLeft size={16} /> {t.back}
      </button>

      <div className="dv-card mb-8 fade-up" style={{ borderLeft: `4px solid ${accent}` }}>
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">{villa.model}</div>
            <h1 className="font-serif-dv text-5xl leading-tight">{villa.name}</h1>
            <p className="text-stone-500 mt-2 max-w-xl">{villa.description}</p>
            {owner && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-stone-50 border border-stone-200">
                <User size={14} weight="light" />
                <span className="text-sm"><b>{t.investorLbl}:</b> {owner.name}</span>
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-stone-500">{t.price}</div>
            <div className="font-serif-dv text-4xl" style={{ color: accent }}>€{Number(villa.price_eur).toLocaleString()}</div>
            <span className={`dv-badge mt-2 inline-block ${villa.status === "sold" ? "dv-badge-baja" : villa.status === "reserved" ? "dv-badge-media" : ""}`}>
              {t.statusLbl[villa.status] || villa.status}
            </span>
            {user?.role === "admin" && (
              <div className="mt-3 flex flex-col gap-1.5 items-end">
                <button
                  onClick={() => setShowEditModal(true)}
                  data-testid="villa-edit-btn"
                  className="text-xs px-3 py-1 rounded border border-stone-300 hover:border-stone-900 text-stone-700 hover:text-stone-900"
                >
                  Editar villa
                </button>
                <button
                  onClick={() => setShowStatusModal(true)}
                  data-testid="villa-change-status-btn"
                  className="text-xs px-3 py-1 rounded border border-stone-300 hover:border-stone-900 text-stone-700 hover:text-stone-900"
                >
                  Cambiar estado
                </button>
                <button
                  onClick={() => setShowPriceModal(true)}
                  data-testid="villa-edit-price-btn"
                  className="text-xs px-3 py-1 rounded border border-stone-300 hover:border-stone-900 text-stone-700 hover:text-stone-900"
                >
                  Editar precio vendido
                </button>
                {villa.owner_user_id && (
                  <button
                    onClick={() => setShowUnassignModal(true)}
                    data-testid="villa-unassign-btn"
                    className="text-xs px-3 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50"
                  >
                    Desasignar inversor
                  </button>
                )}
                <button
                  onClick={() => setShowDeleteModal(true)}
                  data-testid="villa-delete-btn"
                  className="text-xs px-3 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50"
                >
                  Eliminar villa
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {tab === "overview" && (
        <>
          <Section title={t.kpis}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                label={t.kpiProgress}
                value={ownProgress.pct === null ? "—" : `${ownProgress.pct}%`}
                sub={ownProgress.pct === null ? t.pendingUpdate : (t.milestonePhase[ownProgress.phaseKey] || "—")}
                accent={accent}
                testid="villa-stat-progress"
              />
              <StatCard label={t.kpiPaid} value={`€${Math.round(summary.payments_paid).toLocaleString()}`} sub={t.paymentsCount(payments.filter(p=>p.status==="paid").length)} accent={accent} testid="villa-stat-paid" />
              <StatCard label={t.kpiPending} value={`€${Math.round(summary.payments_pending).toLocaleString()}`} sub={t.pendingCount(payments.filter(p=>p.status!=="paid").length)} accent={accent} testid="villa-stat-pending" />
              <StatCard label={t.kpiDocs} value={summary.documents} sub={t.updatesCount(summary.progress_updates)} accent={accent} testid="villa-stat-docs" />
            </div>
          </Section>

          <Section title={t.timelineTitle} subtitle={t.timelineSub}>
            <VillaTimeline progress={progress} payments={payments} t={t} />
          </Section>
        </>
      )}

      {tab === "progress" && user?.role === "admin" && (
        <EstadoDeObraPanel villa={villa} onSaved={reload} />
      )}
      {tab === "progress" && <ProgressTab items={progress} accent={accent} t={t} isAdmin={user?.role === "admin"} onChanged={reload} />}
      {tab === "progress" && user?.role === "admin" && (
        <Section title="Archivos de obra" subtitle="Fotos y documentos subidos por el equipo de obra para esta villa.">
          <UploadsManager villaId={id} />
        </Section>
      )}
      {tab === "documents" && <DocumentsTab documents={documents} villaId={id} accent={accent} isAdmin={user?.role === "admin"} t={t} />}
      {tab === "payments" && <PaymentsTab payments={payments} accent={accent} t={t} isAdmin={user?.role === "admin"} onChanged={reload} />}
      {tab === "invoices" && user?.role !== "investor" && <InvoicesTab invoices={invoices} accent={accent} t={t} />}
      {tab === "gallery" && <GalleryTab progress={progress} t={t} />}

      {showUnassignModal && (
        <UnassignInvestorModal
          villa={villa}
          owner={owner}
          onClose={() => setShowUnassignModal(false)}
          onDone={async () => { setShowUnassignModal(false); await reload(); }}
        />
      )}
      {showPriceModal && (
        <EditSalePriceModal
          villa={villa}
          hasOwner={!!villa.owner_user_id}
          paidPaymentsCount={payments.filter(p => p.status === "paid").length}
          activePaymentsCount={payments.filter(p => p.status !== "archived").length}
          onClose={() => setShowPriceModal(false)}
          onDone={async () => { setShowPriceModal(false); await reload(); }}
        />
      )}
      {showStatusModal && (
        <ChangeStatusModal
          villa={villa}
          onClose={() => setShowStatusModal(false)}
          onDone={async () => { setShowStatusModal(false); await reload(); }}
        />
      )}
      {showEditModal && (
        <EditVillaModal
          villa={villa}
          onClose={() => setShowEditModal(false)}
          onDone={async () => { setShowEditModal(false); await reload(); }}
        />
      )}
      {showDeleteModal && (
        <DeleteVillaModal
          villa={villa}
          onClose={() => setShowDeleteModal(false)}
          onDeleted={() => { setShowDeleteModal(false); navigate("/villas"); }}
        />
      )}
    </Shell>
  );
}

function VillaTimeline({ progress, payments, t }) {
  const events = [
    ...progress.map(p => ({
      date: p.created_at, type: "progress",
      title: t.progressEventTitle(p.phase),
      desc: `${p.percentage}% — ${p.description}`,
      icon: Buildings, color: "#9C5236",
    })),
    ...payments.filter(p => p.status === "paid").map(p => ({
      date: p.created_at, type: "payment",
      title: t.paymentEventTitle(p.phase),
      desc: `€${Number(p.amount).toLocaleString()} ${p.currency}`,
      icon: Coin, color: "#2F3E35",
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  if (events.length === 0) return <div className="dv-card text-stone-400 text-center py-10">{t.noEvents}</div>;

  return (
    <div className="dv-card">
      <div className="relative pl-8">
        <div className="absolute left-3 top-2 bottom-2 w-px bg-stone-200" />
        {events.map((e, i) => (
          <div key={i} className="relative mb-6 last:mb-0">
            <div className="absolute -left-[22px] top-0 w-6 h-6 rounded-full flex items-center justify-center text-white" style={{ background: e.color }}>
              <e.icon size={12} weight="bold" />
            </div>
            <div className="text-xs text-stone-500 tracking-wider uppercase">
              {new Date(e.date).toLocaleDateString(t.dateLocale, { day: "numeric", month: "long", year: "numeric" })}
            </div>
            <div className="font-medium mt-0.5">{e.title}</div>
            <div className="text-sm text-stone-600">{e.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProgressTab({ items, accent, t, isAdmin, onChanged }) {
  const handleDelete = async (it) => {
    const ok = window.confirm(
      `¿Eliminar permanentemente esta entrada de progreso?\n\n` +
      `Fase: ${it.phase} · ${it.percentage}%\n` +
      "También se eliminarán las fotos/archivos vinculados. Esta acción es IRREVERSIBLE."
    );
    if (!ok) return;
    try {
      await api.delete(`/progress/${it.progress_id}?confirm=true`);
      await onChanged?.();
    } catch (e) { alert(formatError(e)); }
  };
  if (items.length === 0) return <Section title={t.progressTitle}><div className="dv-card text-stone-400 text-center py-10">{t.progressDone}</div></Section>;
  return (
    <Section title={t.progressListTitle}>
      <div className="space-y-4">
        {items.map(it => (
          <div key={it.progress_id} className="dv-card" data-testid={`progress-item-${it.progress_id}`}>
            <div className="flex justify-between items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-3 mb-2">
                  <span className="dv-badge">{it.phase}</span>
                  <span className="text-xs text-stone-500">{new Date(it.created_at).toLocaleDateString(t.dateLocale)}</span>
                </div>
                <div className="font-serif-dv text-2xl">{it.percentage}{t.pctCompleted}</div>
                <p className="text-stone-600 mt-1">{it.description}</p>
                {it.uploaded_by_name && <div className="text-xs text-stone-400 mt-2">{t.uploadedBy(it.uploaded_by_name)}</div>}
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="font-serif-dv text-5xl" style={{ color: accent }}>{it.percentage}%</div>
                {isAdmin && (
                  <button
                    onClick={() => handleDelete(it)}
                    data-testid={`progress-delete-${it.progress_id}`}
                    className="text-xs px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-700 hover:text-white hover:border-red-700"
                  >
                    Eliminar
                  </button>
                )}
              </div>
            </div>
            {(it.photo_files?.length > 0 || it.photos_base64?.length > 0) && (() => {
              const photoCount = (it.photo_files?.length || 0) + (!it.photo_files?.length ? (it.photos_base64?.length || 0) : 0);
              const gridCls = photoCount === 1
                ? "grid grid-cols-1 max-w-xs gap-2 mt-4"
                : photoCount <= 3
                ? "grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4"
                : "grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4";
              return (
                <div className={gridCls}>
                  {/* F-32: prefer photo_files (served via /api/files/{id}); fallback to legacy photos_base64 */}
                  {(it.photo_files || []).map((pf) => (
                    <AuthImg
                      key={pf.file_id}
                      fileId={pf.file_id}
                      alt={pf.caption || ""}
                      className="aspect-square object-cover rounded-md"
                    />
                  ))}
                  {(!it.photo_files || it.photo_files.length === 0) && (it.photos_base64 || []).map((p, i) => (
                    <img key={i} src={`data:image/jpeg;base64,${p}`} alt="" className="aspect-square object-cover rounded-md" />
                  ))}
                </div>
              );
            })()}
          </div>
        ))}
      </div>
    </Section>
  );
}

function DocumentsTab({ documents, villaId, accent, isAdmin, t }) {
  const [selected, setSelected] = useState(null);
  const [views, setViews] = useState(null);
  const byCat = documents.reduce((acc, d) => {
    const c = d.category || "other";
    acc[c] = acc[c] || [];
    acc[c].push(d);
    return acc;
  }, {});

  const openDoc = async (doc) => {
    setSelected(doc);
    setViews(null);
    try { await api.get(`/documents/${doc.doc_id}`); } catch {}
    if (isAdmin) {
      try {
        const { data } = await api.get(`/documents/${doc.doc_id}/views`);
        setViews(data);
      } catch {}
    }
  };

  return (
    <Section title={t.dataRoomTitle} subtitle={t.dataRoomSub}>
      {Object.keys(byCat).length === 0 && <div className="dv-card text-stone-400 text-center py-10">{t.noDocs}</div>}
      <div className="space-y-6">
        {Object.entries(byCat).map(([cat, docs]) => (
          <div key={cat}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1 h-4 rounded" style={{ background: accent }} />
              <div className="text-xs tracking-[0.25em] uppercase text-stone-600">{t.categories[cat] || cat} · {docs.length}</div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {docs.map(d => (
                <button key={d.doc_id} onClick={() => openDoc(d)} data-testid={`doc-card-${d.doc_id}`}
                  className="dv-card text-left flex items-center gap-4 hover:-translate-y-0.5 transition-transform">
                  <div className="w-11 h-11 rounded-md flex items-center justify-center shrink-0" style={{ background: `${accent}15`, color: accent }}>
                    <FileText size={22} weight="light" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{d.title}</div>
                    <div className="text-xs text-stone-500">{t.version}{d.version || 1} · {new Date(d.created_at).toLocaleDateString(t.dateLocale)} {d.uploaded_by_name ? `· ${d.uploaded_by_name}` : ""}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setSelected(null)}>
          <div className="dv-card max-w-2xl w-full bg-white" onClick={e => e.stopPropagation()} data-testid="doc-modal">
            <div className="flex items-start justify-between mb-3 gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-stone-500">{t.categories[selected.category] || selected.category}</div>
                <div className="font-serif-dv text-2xl">{selected.title}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-stone-400 hover:text-stone-900 text-2xl leading-none">×</button>
            </div>
            {selected.description && <p className="text-stone-600 text-sm mb-3">{selected.description}</p>}
            <div className="flex gap-4 text-xs text-stone-500 mb-4 flex-wrap">
              <span>{t.docVersion(selected.version || 1)}</span>
              <span>{t.uploadedOn(new Date(selected.created_at).toLocaleDateString(t.dateLocale))}</span>
              {selected.uploaded_by_name && <span>{t.by} {selected.uploaded_by_name}</span>}
            </div>
            {selected.file_url && (
              <a href={selected.file_url} target="_blank" rel="noopener noreferrer"
                className="dv-btn dv-btn-primary w-full" data-testid="doc-open-btn">
                {t.openDoc}
              </a>
            )}
            {views && (
              <div className="mt-5 pt-4 border-t border-stone-200">
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">{t.whoSaw}</div>
                <div className="text-sm text-stone-600 mb-2">{t.viewsLine(views.total, views.unique_users)}</div>
                <div className="max-h-48 overflow-auto">
                  {views.views.slice(0, 10).map((v, i) => (
                    <div key={i} className="text-xs py-1 flex justify-between text-stone-500 border-b border-stone-100 last:border-0">
                      <span>{v.user_name} <span className="text-stone-400">({v.user_role})</span></span>
                      <span>{new Date(v.viewed_at).toLocaleString(t.dateLocale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Section>
  );
}

function PaymentsTab({ payments, accent, t, isAdmin = false, onChanged }) {
  const [showArchived, setShowArchived] = useState(false);
  const archivedCount = useMemo(
    () => payments.filter((p) => p?.status === "archived").length,
    [payments]
  );
  const visible = useMemo(
    () => (showArchived ? payments : payments.filter((p) => p?.status !== "archived")),
    [payments, showArchived]
  );

  if (payments.length === 0) {
    return <Section title={t.paymentsTitle}><div className="dv-card text-stone-400 text-center py-10">{t.paymentsEmpty}</div></Section>;
  }
  return (
    <Section title={t.paymentsSchedule}>
      {isAdmin && archivedCount > 0 && (
        <div className="flex justify-end mb-2 text-xs" data-testid="villa-payments-archived-toggle-row">
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className="text-stone-500 hover:text-stone-800 inline-flex items-center gap-1"
            data-testid="villa-payments-toggle-archived"
          >
            {showArchived
              ? `Ocultar archivados (${archivedCount})`
              : `${archivedCount} pago${archivedCount === 1 ? "" : "s"} archivado${archivedCount === 1 ? "" : "s"} oculto${archivedCount === 1 ? "" : "s"} · Mostrar`}
          </button>
        </div>
      )}
      <div className="dv-card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b border-stone-200 text-left">
            <tr className="text-xs tracking-wider uppercase text-stone-500">
              <th className="px-5 py-3">{isAdmin ? "Pago" : t.phase}</th>
              <th className="px-5 py-3">{t.dueDate}</th>
              <th className="px-5 py-3 text-right">{t.amount}</th>
              <th className="px-5 py-3">{t.status}</th>
              {isAdmin && <th className="px-5 py-3 text-right">Admin</th>}
            </tr>
          </thead>
          <tbody>
            {visible.map(p => {
              const isArchived = p?.status === "archived";
              return (
                <tr
                  key={p.payment_id}
                  className={`border-b border-stone-100 ${isArchived ? "opacity-60" : ""}`}
                  data-testid={`admin-payment-row-${p.payment_id}`}
                >
                  <td className="px-5 py-4 font-medium">{paymentLabel(p)}</td>
                  <td className="px-5 py-4 text-stone-600">{p.due_date || "—"}</td>
                  <td className="px-5 py-4 text-right font-serif-dv text-lg" style={{ color: accent }}>
                    {Number(p.amount).toLocaleString()} {p.currency}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`dv-badge ${p.status === "paid" ? "dv-badge-baja" : p.status === "overdue" ? "dv-badge-alta" : p.status === "archived" ? "" : "dv-badge-media"}`}>
                      {t.payStatus[p.status] || (p.status === "archived" ? "Archivado" : p.status)}
                    </span>
                  </td>
                  {isAdmin && (
                    <td className="px-5 py-4 text-right">
                      <AdminPaymentRowActions payment={p} onChanged={onChanged} />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function AdminPaymentRowActions({ payment, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState(null); // null | "archive" | "delete"
  const isPaid = payment.status === "paid";

  const run = async (kind) => {
    setBusy(true); setErr("");
    try {
      if (kind === "archive") {
        await api.post(`/payments/${payment.payment_id}/archive${isPaid ? "?force=true" : ""}`);
      } else {
        const qs = isPaid ? "?confirm=true&force=true" : "?confirm=true";
        await api.delete(`/payments/${payment.payment_id}${qs}`);
      }
      setMode(null);
      await onChanged?.();
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  if (mode) {
    return (
      <div className="inline-flex items-center gap-1.5 text-[11px]" data-testid={`admin-payment-confirm-${payment.payment_id}`}>
        {err ? (
          <span className="text-red-700">{err.slice(0, 40)}</span>
        ) : (
          <>
            {isPaid && (
              <span className="text-red-700 font-medium">¡Pagado!</span>
            )}
            <span className="text-stone-700">¿Quitar del plan?</span>
            <button
              onClick={() => run("archive")}
              disabled={busy}
              data-testid={`admin-payment-confirm-yes-${payment.payment_id}`}
              className="px-2 py-0.5 rounded text-white disabled:opacity-50 bg-stone-900 hover:bg-stone-800"
            >Sí</button>
            <button
              onClick={() => { setMode(null); setErr(""); }}
              disabled={busy}
              className="px-2 py-0.5 rounded border border-stone-300 text-stone-700 hover:bg-stone-50"
            >No</button>
          </>
        )}
      </div>
    );
  }

  // Hard-delete button intentionally hidden from the UI. The backend endpoint
  // (DELETE /payments/{id}) still exists for scripts/data cleanup, but admins
  // should always go through soft archive from this surface.
  return (
    <div className="inline-flex gap-1.5 text-[11px]">
      {payment.status !== "archived" && (
        <button
          onClick={() => setMode("archive")}
          data-testid={`admin-payment-archive-${payment.payment_id}`}
          className="px-2 py-0.5 rounded border border-stone-300 text-stone-700 hover:border-stone-900 hover:text-stone-900"
        >Quitar del plan</button>
      )}
    </div>
  );
}

function InvoicesTab({ invoices, accent, t }) {
  return (
    <Section title={t.invoicesTitle}>
      {invoices.length === 0 ? (
        <div className="dv-card text-stone-400 text-center py-10">{t.invoicesEmpty}</div>
      ) : (
        <div className="dv-card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200 text-left">
              <tr className="text-xs tracking-wider uppercase text-stone-500">
                <th className="px-4 py-3">{t.invoiceDate}</th><th className="px-4 py-3">{t.invoiceProvider}</th>
                <th className="px-4 py-3">{t.invoiceConcept}</th><th className="px-4 py-3 text-right">{t.amount}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(i => (
                <tr key={i.invoice_id} className="border-b border-stone-100">
                  <td className="px-4 py-3">{i.date}</td>
                  <td className="px-4 py-3 font-medium">{i.provider}</td>
                  <td className="px-4 py-3 text-stone-600">{i.concept}</td>
                  <td className="px-4 py-3 text-right font-serif-dv text-lg" style={{ color: accent }}>{Number(i.amount || 0).toLocaleString()} {i.currency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function GalleryTab({ progress, t }) {
  // F-32: prefer photo_files (served via /api/files/{id}); fallback to legacy photos_base64.
  // Each photo entry carries a `kind` so the renderer knows how to display it.
  const allPhotos = progress.flatMap(p => {
    if (p.photo_files && p.photo_files.length > 0) {
      return p.photo_files.map(pf => ({
        kind: "file", fileId: pf.file_id,
        date: p.created_at, phase: p.phase,
      }));
    }
    return (p.photos_base64 || []).map(b => ({
      kind: "base64", b64: b,
      date: p.created_at, phase: p.phase,
    }));
  });
  if (allPhotos.length === 0) return (
    <Section title={t.galleryTitle}>
      <div className="dv-card text-stone-400 text-center py-12">
        <ImageIcon size={40} weight="light" className="mx-auto mb-3 text-stone-300" />
        {t.galleryEmpty}
      </div>
    </Section>
  );
  return (
    <Section title={t.galleryCount(allPhotos.length)}>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {allPhotos.map((p, i) => (
          <div key={i} className="relative group">
            {p.kind === "file" ? (
              <AuthImg fileId={p.fileId} className="aspect-square object-cover rounded-md w-full" />
            ) : (
              <img src={`data:image/jpeg;base64,${p.b64}`} alt="" className="aspect-square object-cover rounded-md" />
            )}
            <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity rounded-b-md">
              <div className="font-medium">{p.phase}</div>
              <div>{new Date(p.date).toLocaleDateString(t.dateLocale)}</div>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}


/**
 * UnassignInvestorModal — admin-only modal for removing an investor from a villa.
 * Offers three payment policies with clear explanations.
 */
function UnassignInvestorModal({ villa, owner, onClose, onDone }) {
  const [policy, setPolicy] = useState("archive_auto");
  const [forcePaid, setForcePaid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/villas/${villa.villa_id}/unassign-investor`, {
        payment_policy: policy,
        force_overwrite_paid: forcePaid,
      });
      await onDone?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(typeof detail === "object" ? (detail.message || JSON.stringify(detail)) : formatError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
         data-testid="unassign-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="dv-card max-w-lg w-full !p-6">
        <h3 className="font-serif-dv text-2xl mb-1">Desasignar inversor</h3>
        <p className="text-sm text-stone-600 mb-4">
          Villa: <b>{villa.name}</b>
          {owner && <> · Inversor actual: <b>{owner.name}</b></>}
        </p>

        <div className="space-y-2 mb-4">
          <label className="flex items-start gap-2 p-2.5 rounded border border-stone-200 hover:bg-stone-50 cursor-pointer has-[:checked]:border-stone-900 has-[:checked]:bg-stone-50">
            <input type="radio" name="policy" value="archive_auto" checked={policy === "archive_auto"} onChange={() => setPolicy("archive_auto")} className="mt-1" />
            <span>
              <span className="font-medium text-sm">Archivar pagos auto-generados</span>
              <span className="block text-xs text-stone-500">Los pagos pagados se mantienen. Los pendientes del plan se archivan. Recomendado.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 p-2.5 rounded border border-stone-200 hover:bg-stone-50 cursor-pointer has-[:checked]:border-stone-900 has-[:checked]:bg-stone-50">
            <input type="radio" name="policy" value="keep_all" checked={policy === "keep_all"} onChange={() => setPolicy("keep_all")} className="mt-1" />
            <span>
              <span className="font-medium text-sm">Mantener todos los pagos</span>
              <span className="block text-xs text-stone-500">Usar si el inversor sigue debiendo el plan pese a quitarle la villa.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 p-2.5 rounded border border-stone-200 hover:bg-red-50 cursor-pointer has-[:checked]:border-red-300 has-[:checked]:bg-red-50">
            <input type="radio" name="policy" value="delete_auto" checked={policy === "delete_auto"} onChange={() => setPolicy("delete_auto")} className="mt-1" />
            <span>
              <span className="font-medium text-sm text-red-800">Borrar pagos auto-generados</span>
              <span className="block text-xs text-stone-500">Pensado para limpiar datos de prueba. Irreversible.</span>
            </span>
          </label>
        </div>

        {policy === "delete_auto" && (
          <label className="flex items-center gap-2 text-xs text-red-800 mb-3 p-2 bg-red-50 border border-red-200 rounded">
            <input type="checkbox" checked={forcePaid} onChange={(e) => setForcePaid(e.target.checked)} />
            Sobrescribir también pagos ya marcados como pagados (si los hay)
          </label>
        )}

        {err && <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded p-2 mb-3">{err}</div>}

        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  className="text-sm px-3 py-1.5 rounded border border-stone-300 text-stone-700 hover:bg-stone-50">
            Cancelar
          </button>
          <button onClick={submit} disabled={busy}
                  data-testid="unassign-submit"
                  className="text-sm px-3 py-1.5 rounded bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50">
            {busy ? "Aplicando…" : "Desasignar"}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * EditSalePriceModal — admin-only modal for editing `price_eur`.
 * Shows before/after diff and the payment policy selector.
 */
function EditSalePriceModal({ villa, hasOwner, paidPaymentsCount, activePaymentsCount = 0, onClose, onDone }) {
  const [newPrice, setNewPrice] = useState(String(villa.price_eur || ""));
  const [reason, setReason] = useState("");
  const [policy, setPolicy] = useState("keep_existing");
  const [forcePaid, setForcePaid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Custom payment plan editor — only relevant when policy === "custom".
  // Each row has its own `pct` for the UI; the backend only receives amount.
  const [customRows, setCustomRows] = useState([
    { title: "", pct: "", amount: "", due_date: "", status: "pending", phase: "" },
  ]);

  const newPriceNum = Number(newPrice);
  const diff = newPriceNum - (villa.price_eur || 0);
  const diffPct = villa.price_eur ? (diff / villa.price_eur) * 100 : 0;

  const customSum = useMemo(
    () => customRows.reduce((s, r) => s + (Number(r.amount) || 0), 0),
    [customRows]
  );
  const customDiff = customSum - (Number.isFinite(newPriceNum) ? newPriceNum : 0);
  const customMatches = Math.abs(customDiff) <= 1.0;
  const customRowsValid = customRows.every(
    (r) => String(r.title).trim().length > 0 && Number(r.amount) > 0
  );

  const validationErrors = [];
  if (!(newPriceNum > 0)) validationErrors.push("Introduce un precio válido");
  if (reason.trim().length < 5) validationErrors.push("El motivo debe tener al menos 5 caracteres");
  if (policy === "custom") {
    if (customRows.length === 0) validationErrors.push("Añade al menos un pago al plan personalizado");
    if (!customRowsValid) validationErrors.push("Cada pago necesita un nombre y un importe > 0");
    if (!customMatches) validationErrors.push("La suma del plan no cuadra con el precio vendido (tolerancia 1€)");
  }
  const canSubmit = !busy && validationErrors.length === 0;

  // Helpers for the custom rows editor. Percentage is just a UI shortcut for
  // recomputing `amount`. When admin types an amount we don't write back into
  // `pct` (avoids fighting the user); the resumen below shows the live sum.
  const updateRow = (i, patch) => {
    setCustomRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const onPctChange = (i, raw) => {
    const pct = raw;
    const pctNum = Number(raw);
    if (Number.isFinite(pctNum) && Number.isFinite(newPriceNum) && newPriceNum > 0) {
      const amt = Math.round(newPriceNum * (pctNum / 100) * 100) / 100;
      updateRow(i, { pct, amount: String(amt) });
    } else {
      updateRow(i, { pct });
    }
  };
  const addRow = () =>
    setCustomRows((rows) => [
      ...rows,
      { title: "", pct: "", amount: "", due_date: "", status: "pending", phase: "" },
    ]);
  const removeRow = (i) =>
    setCustomRows((rows) => (rows.length <= 1 ? rows : rows.filter((_, idx) => idx !== i)));

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const payload = {
        new_price_eur: newPriceNum,
        reason: reason.trim(),
        payment_policy: policy,
        force_overwrite_paid: forcePaid,
      };
      if (policy === "custom") {
        // Extra confirm when there are active payments that will be archived.
        if (activePaymentsCount > 0) {
          const ok = window.confirm(
            `Se archivarán ${activePaymentsCount} pago(s) activo(s) y se creará el nuevo plan personalizado. ¿Continuar?`
          );
          if (!ok) { setBusy(false); return; }
        }
        payload.custom_payments = customRows.map((r) => ({
          title: String(r.title).trim(),
          amount: Number(r.amount),
          due_date: r.due_date || null,
          status: r.status || "pending",
          phase: r.phase ? r.phase : null,
        }));
      }
      await api.patch(`/villas/${villa.villa_id}/sale-price`, payload);
      await onDone?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(typeof detail === "object" ? (detail.message || JSON.stringify(detail)) : formatError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
         data-testid="sale-price-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="dv-card max-w-lg w-full !p-6">
        <h3 className="font-serif-dv text-2xl mb-1">Editar precio vendido</h3>
        <p className="text-sm text-stone-600 mb-4">Villa: <b>{villa.name}</b></p>

        <div className="space-y-3 mb-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500">Nuevo precio (EUR)</label>
            <input
              type="number" min="1" step="1"
              value={newPrice}
              onChange={(e) => setNewPrice(e.target.value)}
              data-testid="sale-price-input"
              className="w-full border border-stone-300 rounded px-3 py-2 text-lg font-serif-dv focus:border-stone-900 focus:outline-none"
            />
            <div className="mt-1 text-xs text-stone-500">
              Antes: €{Number(villa.price_eur || 0).toLocaleString()}{" "}
              {newPriceNum > 0 && newPriceNum !== villa.price_eur && (
                <span className={diff >= 0 ? "text-emerald-700" : "text-red-700"}>
                  · Diff: {diff >= 0 ? "+" : ""}€{Math.abs(diff).toLocaleString()} ({diffPct.toFixed(1)}%)
                </span>
              )}
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500">Motivo (≥5 chars, requerido)</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej. Reconciliación: precio acordado final"
              data-testid="sale-price-reason"
              className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:border-stone-900 focus:outline-none"
            />
          </div>

          {hasOwner && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-stone-500">Plan de pagos</label>
              <div className="space-y-2 mt-1">
                <label className="flex items-start gap-2 p-2.5 rounded border border-stone-200 hover:bg-stone-50 cursor-pointer has-[:checked]:border-stone-900 has-[:checked]:bg-stone-50">
                  <input type="radio" name="pp" value="keep_existing" checked={policy === "keep_existing"} onChange={() => setPolicy("keep_existing")} className="mt-1" />
                  <span>
                    <span className="font-medium text-sm">Mantener pagos existentes</span>
                    <span className="block text-xs text-stone-500">Solo actualiza el precio. No toca pagos. Los importes seguirán sumando lo anterior.</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 p-2.5 rounded border border-stone-200 hover:bg-stone-50 cursor-pointer has-[:checked]:border-stone-900 has-[:checked]:bg-stone-50">
                  <input type="radio" name="pp" value="regenerate" checked={policy === "regenerate"} onChange={() => setPolicy("regenerate")} className="mt-1" />
                  <span>
                    <span className="font-medium text-sm">Regenerar plan 10/40/30/20</span>
                    <span className="block text-xs text-stone-500">Archiva pagos pendientes auto-generados y crea 4 nuevos con el nuevo precio.</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 p-2.5 rounded border border-stone-200 hover:bg-stone-50 cursor-pointer has-[:checked]:border-stone-900 has-[:checked]:bg-stone-50" data-testid="sale-price-policy-custom-label">
                  <input
                    type="radio"
                    name="pp"
                    value="custom"
                    checked={policy === "custom"}
                    onChange={() => setPolicy("custom")}
                    className="mt-1"
                    data-testid="sale-price-policy-custom"
                  />
                  <span>
                    <span className="font-medium text-sm">Personalizar plan de pagos</span>
                    <span className="block text-xs text-stone-500">Archiva todos los pagos activos del inversor y crea un plan con las filas que definas abajo.</span>
                  </span>
                </label>
              </div>

              {(policy === "regenerate" || policy === "custom") && paidPaymentsCount > 0 && (
                <label
                  className={`flex items-start gap-3 text-xs mt-2 p-3 rounded border cursor-pointer transition-colors ${
                    forcePaid
                      ? "bg-red-100 border-red-400 text-red-900"
                      : "bg-stone-50 border-stone-300 text-stone-700 hover:bg-stone-100"
                  }`}
                  data-testid="sale-price-force-paid-label"
                >
                  <input
                    type="checkbox"
                    checked={forcePaid}
                    onChange={(e) => setForcePaid(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-red-600"
                    data-testid="sale-price-force-paid"
                  />
                  <span className="flex-1">
                    <span className="block font-medium">
                      Sobrescribir también los {paidPaymentsCount} pago(s) ya pagado(s)
                    </span>
                    <span className="block mt-0.5 opacity-80">
                      {forcePaid
                        ? "Activado — los pagos pagados serán reemplazados. Acción irreversible."
                        : "Desactivado — los pagos pagados se mantienen intactos."}
                    </span>
                  </span>
                </label>
              )}

              {policy === "custom" && (
                <div className="mt-3 border border-stone-200 rounded p-3 bg-stone-50/50" data-testid="sale-price-custom-editor">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-widest text-stone-500">Filas del plan</div>
                    <button
                      type="button"
                      onClick={addRow}
                      className="text-xs px-2 py-1 rounded border border-stone-300 text-stone-700 hover:bg-stone-100 inline-flex items-center gap-1"
                      data-testid="sale-price-custom-add"
                    >
                      <Plus size={12} /> Añadir pago
                    </button>
                  </div>
                  <div className="space-y-2">
                    {customRows.map((row, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`sale-price-custom-row-${i}`}>
                        <input
                          type="text"
                          placeholder="Nombre del pago *"
                          value={row.title}
                          onChange={(e) => updateRow(i, { title: e.target.value })}
                          className="col-span-5 border border-stone-300 rounded px-2 py-1.5 text-sm"
                          maxLength={200}
                          data-testid={`sale-price-custom-title-${i}`}
                        />
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="%"
                          value={row.pct}
                          onChange={(e) => onPctChange(i, e.target.value)}
                          className="col-span-2 border border-stone-300 rounded px-2 py-1.5 text-sm"
                          data-testid={`sale-price-custom-pct-${i}`}
                        />
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="€"
                          value={row.amount}
                          onChange={(e) => updateRow(i, { amount: e.target.value })}
                          className="col-span-2 border border-stone-300 rounded px-2 py-1.5 text-sm"
                          data-testid={`sale-price-custom-amount-${i}`}
                        />
                        <input
                          type="date"
                          value={row.due_date}
                          onChange={(e) => updateRow(i, { due_date: e.target.value })}
                          className="col-span-2 border border-stone-300 rounded px-2 py-1.5 text-sm"
                          data-testid={`sale-price-custom-due-${i}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeRow(i)}
                          disabled={customRows.length <= 1}
                          className="col-span-1 inline-flex items-center justify-center w-7 h-7 rounded border border-stone-300 text-stone-500 hover:text-red-700 hover:border-red-300 disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Quitar fila"
                          data-testid={`sale-price-custom-remove-${i}`}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="mt-3 grid grid-cols-3 gap-2 text-xs" data-testid="sale-price-custom-summary">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-stone-500">Suma plan</div>
                      <div className="font-medium">€{Math.round(customSum).toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-stone-500">Precio vendido</div>
                      <div className="font-medium">€{Math.round(newPriceNum || 0).toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-stone-500">Diferencia</div>
                      <div className={customMatches ? "font-medium text-emerald-700" : "font-medium text-amber-800"}>
                        {Math.abs(customDiff) < 0.5 ? "—" : `${customDiff > 0 ? "+" : ""}€${Math.round(customDiff).toLocaleString()}`}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2">
                    {customMatches ? (
                      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="sale-price-custom-match-ok">
                        ✓ Cuadra
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-200" data-testid="sale-price-custom-match-warn">
                        ⚠ No cuadra
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {err && <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded p-2 mb-3">{err}</div>}

        {validationErrors.length > 0 && (
          <ul
            className="bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded p-2 mb-3 space-y-0.5 list-disc list-inside"
            data-testid="sale-price-validation-errors"
          >
            {validationErrors.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        )}

        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  className="text-sm px-3 py-1.5 rounded border border-stone-300 text-stone-700 hover:bg-stone-50">
            Cancelar
          </button>
          <button onClick={submit} disabled={!canSubmit}
                  data-testid="sale-price-submit"
                  className="text-sm px-3 py-1.5 rounded bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed">
            {busy ? "Guardando…" : "Confirmar cambio"}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * ChangeStatusModal — admin-only modal for manually setting the villa status.
 * Used when state has desynced from reality (e.g. a sale is closed but the
 * villa is still marked "available", or a reservation was cancelled).
 */
function ChangeStatusModal({ villa, onClose, onDone }) {
  const STATUSES = [
    { value: "available", label: "Disponible", help: "Visible públicamente, sin inversor asignado." },
    { value: "reserved", label: "Reservada", help: "Inversor en proceso, ya no se ofrece a otros." },
    { value: "sold", label: "Vendida", help: "Operación cerrada. Suele acompañar a la asignación de inversor." },
  ];
  const [newStatus, setNewStatus] = useState(villa.status || "available");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const changed = newStatus !== villa.status;

  const submit = async () => {
    if (!changed) { onClose?.(); return; }
    setBusy(true); setErr("");
    try {
      await api.patch(`/villas/${villa.villa_id}/status`, {
        status: newStatus,
        reason: reason.trim() || undefined,
      });
      await onDone?.();
    } catch (e) {
      setErr(formatError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
         data-testid="status-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="dv-card max-w-lg w-full !p-6">
        <h3 className="font-serif-dv text-2xl mb-1">Cambiar estado de la villa</h3>
        <p className="text-sm text-stone-600 mb-4">
          Villa: <b>{villa.name}</b> · Estado actual: <b>{villa.status}</b>
        </p>

        <div className="space-y-2 mb-4">
          {STATUSES.map(s => (
            <label
              key={s.value}
              className="flex items-start gap-2 p-2.5 rounded border border-stone-200 hover:bg-stone-50 cursor-pointer has-[:checked]:border-stone-900 has-[:checked]:bg-stone-50"
            >
              <input
                type="radio" name="status" value={s.value}
                checked={newStatus === s.value}
                onChange={() => setNewStatus(s.value)}
                className="mt-1"
                data-testid={`status-radio-${s.value}`}
              />
              <span>
                <span className="font-medium text-sm">{s.label}</span>
                <span className="block text-xs text-stone-500">{s.help}</span>
              </span>
            </label>
          ))}
        </div>

        <div className="mb-4">
          <label className="text-[10px] uppercase tracking-widest text-stone-500">Motivo (opcional)</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ej. Cancelación de reserva por inversor"
            data-testid="status-reason"
            className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:border-stone-900 focus:outline-none"
          />
        </div>

        {err && <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded p-2 mb-3">{err}</div>}

        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  className="text-sm px-3 py-1.5 rounded border border-stone-300 text-stone-700 hover:bg-stone-50">
            Cancelar
          </button>
          <button onClick={submit} disabled={busy || !changed}
                  data-testid="status-submit"
                  className="text-sm px-3 py-1.5 rounded bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50">
            {busy ? "Guardando…" : "Guardar cambio"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * EditVillaModal — admin-only modal to edit basic villa fields (name, model,
 * description, price). Does NOT change status (use ChangeStatusModal) nor
 * sale-price-with-payment-regeneration (use EditSalePriceModal).
 */
function EditVillaModal({ villa, onClose, onDone }) {
  const [name, setName] = useState(villa.name || "");
  const [model, setModel] = useState(villa.model || "Dolce Vistas");
  const [description, setDescription] = useState(villa.description || "");
  const [priceEur, setPriceEur] = useState(String(villa.price_eur ?? ""));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const update = {};
      if (name.trim() !== (villa.name || "")) update.name = name.trim();
      if (model !== villa.model) update.model = model;
      if (description !== (villa.description || "")) update.description = description;
      const newPrice = Number(priceEur);
      if (newPrice > 0 && newPrice !== villa.price_eur) update.price_eur = newPrice;
      if (Object.keys(update).length === 0) { onClose?.(); return; }
      await api.patch(`/villas/${villa.villa_id}`, update);
      await onDone?.();
    } catch (e) {
      setErr(formatError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
         data-testid="edit-villa-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="dv-card max-w-lg w-full !p-6">
        <h3 className="font-serif-dv text-2xl mb-1">Editar villa</h3>
        <p className="text-sm text-stone-600 mb-4">Datos básicos de <b>{villa.name}</b></p>

        <div className="space-y-3 mb-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500">Nombre</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                   data-testid="edit-villa-name"
                   className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:border-stone-900 focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500">Modelo</label>
            <select value={model} onChange={(e) => setModel(e.target.value)}
                    data-testid="edit-villa-model"
                    className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:border-stone-900 focus:outline-none">
              <option>Dolce Vistas</option>
              <option>Dolce Privè</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500">Precio (EUR)</label>
            <input type="number" min="1" step="1" value={priceEur} onChange={(e) => setPriceEur(e.target.value)}
                   data-testid="edit-villa-price"
                   className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:border-stone-900 focus:outline-none" />
            <div className="text-[10px] text-stone-400 mt-1">
              Para reajustar pagos al cambiar el precio, usa "Editar precio vendido" en su lugar.
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500">Descripción</label>
            <textarea rows="3" value={description} onChange={(e) => setDescription(e.target.value)}
                      data-testid="edit-villa-description"
                      className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:border-stone-900 focus:outline-none" />
          </div>
        </div>

        {err && <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded p-2 mb-3">{err}</div>}

        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  className="text-sm px-3 py-1.5 rounded border border-stone-300 text-stone-700 hover:bg-stone-50">
            Cancelar
          </button>
          <button onClick={submit} disabled={busy}
                  data-testid="edit-villa-submit"
                  className="text-sm px-3 py-1.5 rounded bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50">
            {busy ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * DeleteVillaModal — admin-only modal that fetches the villa-impact preview
 * and refuses the deletion when the villa still has relations
 * (owner, payments, invoices, documents, progress, tasks).
 */
function DeleteVillaModal({ villa, onClose, onDeleted }) {
  const [impact, setImpact] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setImpact(null); setErr("");
    api.get(`/villas/${villa.villa_id}/impact`)
      .then(r => { if (!cancelled) setImpact(r.data); })
      .catch(e => { if (!cancelled) setErr(formatError(e)); });
    return () => { cancelled = true; };
  }, [villa.villa_id]);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.delete(`/villas/${villa.villa_id}?confirm=true`);
      onDeleted?.();
    } catch (e) {
      setErr(formatError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
         data-testid="delete-villa-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="dv-card max-w-lg w-full !p-6 bg-white">
        <h3 className="font-serif-dv text-2xl mb-1">Eliminar villa</h3>
        <p className="text-sm text-stone-600 mb-4">{villa.name}</p>

        {!impact && !err && (
          <div className="text-sm text-stone-500 py-6 text-center">Calculando impacto…</div>
        )}

        {impact && (
          <>
            {impact.blockers && impact.blockers.length > 0 ? (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm">
                <div className="font-medium text-red-900 mb-1">No se puede eliminar:</div>
                <ul className="list-disc pl-5 space-y-1 text-red-800">
                  {impact.blockers.map(b => (
                    <li key={b.code} data-testid={`villa-blocker-${b.code}`}>{b.message}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
                Esta villa no tiene relaciones activas. La acción es <b>irreversible</b>.
              </div>
            )}

            <div className="text-xs text-stone-500 mb-4">
              Una villa se considera "eliminable" sólo cuando no tiene inversor asignado, ni pagos, ni facturas, ni documentos en data room, ni progresos ni tareas.
            </div>
          </>
        )}

        {err && <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded p-2 mb-3">{err}</div>}

        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  className="text-sm px-3 py-1.5 rounded border border-stone-300 text-stone-700 hover:bg-stone-50">
            Cancelar
          </button>
          <button onClick={submit}
                  disabled={busy || !impact || !impact.can_delete}
                  data-testid="delete-villa-submit"
                  className="text-sm px-3 py-1.5 rounded bg-red-700 text-white hover:bg-red-800 disabled:opacity-50">
            {busy ? "Eliminando…" : "Eliminar definitivamente"}
          </button>
        </div>
      </div>
    </div>
  );
}

